import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { CrudType } from '../../../utility/types/CrudType';
import { CrudStatus } from '../../../utility/types/CrudStatus';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../../envConfig';
import tagType from '../../../utility/types/tagType';

const selectTagListCrudOperation = (state) => state.tagList.crudOperation;

const selectTagListCrudStatus = (state) => state.tagList.crudStatus;

const selectTagListLoading = (state) => state.tagList.loading;

const selectTagList = createSelector([
  (state) => state.tagList.tagList], (tagList) => tagList);

const selectAllTagsByTagIdList = createSelector([
  selectTagList,
  (_, tagIdList) => tagIdList], (tagList, tagIdList) => tagList
  .filter((tag) => tagIdList.includes(tag.id)));

const fetchTagList = createAsyncThunk(
  'securedApi/tagList/fetchTagList',
  async (_, { rejectWithValue }) => {
    try {
      return await RaaAuthenticatedRemoteService.globalInstance()
        .get(`${envConfig.apiUrl}/api/v1/tags`);
    } catch (error) {
      return rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data,
      });
    }
  },
);

const fetchTag = createAsyncThunk(
  'securedApi/tagList/fetchTag',
  async (tagId, { rejectWithValue }) => {
    try {
      return await RaaAuthenticatedRemoteService.globalInstance()
        .get(`${envConfig.apiUrl}/api/v1/tags/${tagId}`);
    } catch (error) {
      return rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data,
      });
    }
  },
);

const createTag = createAsyncThunk(
  'securedApi/tagList/createTag',
  async (tagData, thunkAPI) => {
    const { tag, subscriptionId } = tagData;
    try {
      const { id } = await RaaAuthenticatedRemoteService.globalInstance()
        .post(`${envConfig.apiUrl}/api/v1/tags`,
          { data: { ...tag } });
      return thunkAPI.fulfillWithValue({ ...tag, id, subscriptionId });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const editTag = createAsyncThunk(
  'securedApi/tagList/editTag',
  async (tagPatchData, thunkAPI) => {
    const { editedFields, editedTagId, patchFields } = tagPatchData;
    try {
      return await RaaAuthenticatedRemoteService.globalInstance()
        .patch(`${envConfig.apiUrl}/api/v1/tags/${editedTagId}`,
          { data: { ...editedFields, patchFields } });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const editTags = createAsyncThunk(
  'securedApi/tagList/editTags',
  async (tagsPatchData, thunkAPI) => {
    try {
      return await RaaAuthenticatedRemoteService.globalInstance()
        .post(`${envConfig.apiUrl}/api/v1/tags/bulk/`,
          { data: { ...tagsPatchData } });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const deleteTag = createAsyncThunk(
  'securedApi/tagList/deleteTag',
  async (id, thunkAPI) => {
    try {
      await RaaAuthenticatedRemoteService.globalInstance()
        .delete(`${envConfig.apiUrl}/api/v1/tags/${id}`);
      return thunkAPI.fulfillWithValue(id);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data,
      });
    }
  },
);

const initialState = {
  tagList: [],
  crudOperation: CrudType.READ,
  crudStatus: CrudStatus.IDLE,
  error: false,
  loading: true,
};

const tagListSlice = createSlice({
  name: 'tagList',
  initialState,
  reducers: {
    clearTagListSlice: () => initialState,
    removeTag: (state, action) => {
      state.tagList = [...state.tagList]
        .filter((tag) => tag.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTagList.pending, (state) => {
      state.crudOperation = CrudType.BACKGROUND_READ;
      state.crudStatus = CrudStatus.PENDING;
    })
      .addCase(fetchTag.pending, (state) => {
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(fetchTag.fulfilled, (state, action) => {
        const { tag } = action.payload;
        const foundIndex = state.tagList
          .findIndex((t) => t.id === tag.id);
        if (foundIndex !== -1) {
          state.tagList.splice(foundIndex, 1, tag);
        } else {
          state.tagList.push(tag);
        }
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(fetchTag.rejected, (state) => {
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      })
      .addCase(fetchTagList.fulfilled, (state, action) => {
        state.tagList = action.payload.tags;
        state.loading = false;
        state.crudStatus = CrudStatus.SUCCESS;
      })
      .addCase(fetchTagList.rejected, (state) => {
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
        state.loading = false;
      })
      .addCase(deleteTag.pending, (state) => {
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(deleteTag.fulfilled, (state, action) => {
        state.tagList = [...state.tagList]
          .filter((tag) => tag.id !== action.payload);
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(deleteTag.rejected, (state) => {
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      })
      .addCase(editTag.pending, (state) => {
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(editTag.fulfilled, (state, action) => {
        const newTag = action.payload.tag;
        const foundIndex = state.tagList
          .findIndex((range) => range.id === newTag.id);
        if (foundIndex !== -1) {
          state.tagList.splice(foundIndex, 1, newTag);
        } else {
          state.tagList.push(newTag);
        }
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(editTag.rejected, (state) => {
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      })
      .addCase(editTags.pending, (state) => {
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(editTags.fulfilled, (state) => {
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(editTags.rejected, (state) => {
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      })
      .addCase(createTag.pending, (state) => {
        state.crudOperation = CrudType.CREATE;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(createTag.fulfilled, (state, action) => {
        const newTag = {
          id: action.payload.id,
          subscriptionId: action.payload.subscriptionId,
          name: action.payload.name,
          type: tagType.USER_DEFINED,
        };
        const foundIndex = state.tagList
          .findIndex((range) => range.id === newTag.id);
        if (foundIndex !== -1) {
          state.tagList.splice(foundIndex, 1, newTag);
        } else {
          state.tagList.push(newTag);
        }
        state.crudOperation = CrudType.CREATE;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(createTag.rejected, (state) => {
        state.crudOperation = CrudType.CREATE;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      });
  },
});

const { actions, reducer } = tagListSlice;

export const {
  clearTagListSlice,
  removeTag,
} = actions;
export {
  selectTagList,
  fetchTagList,
  deleteTag,
  editTag,
  fetchTag,
  editTags,
  createTag,
  selectTagListCrudStatus,
  selectTagListCrudOperation,
  selectTagListLoading,
  selectAllTagsByTagIdList,
};
export default reducer;
